import React, { useMemo, useState } from 'react';
import { Search, RefreshCw, ArrowDown, ArrowUp, Check, Star, Calendar, Send, MessageSquare, Film, Edit2, BookOpen, HeartHandshake, Users, AlertCircle } from 'lucide-react';
import { COLORS } from '@/data';
import type { AdminCoupleSummary, AdminPeople, AdminReviewerSummary } from '@/types';
import { activityState, coupleAttention, coupleName, deviceLabel, lastActiveAt, ms, reviewerPending, shortDate, timeAgo } from './people-format';
import type { ActivityState, Person } from './people-format';

interface PeopleOverviewProps {
  people: AdminPeople | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onOpenCouple: (coupleId: string) => void;
  onOpenReviewer: (reviewerId: string) => void;
}

type Segment = 'couples' | 'reviewers';
type Filter = 'all' | 'online' | 'active' | 'dormant' | 'attention';
type SortDir = 'asc' | 'desc';

// ---- Sorting ----

type CoupleSortKey = 'name' | 'lastActive' | 'logins' | 'joined' | 'engagement' | 'submissions' | 'attention';
type ReviewerSortKey = 'name' | 'lastActive' | 'logins' | 'joined' | 'clipReviews' | 'submissionReviews' | 'pending';

const COUPLE_SORT: Record<CoupleSortKey, (c: AdminCoupleSummary) => string | number> = {
  name: c => coupleName(c).toLowerCase(),
  lastActive: c => ms(lastActiveAt(c)),
  logins: c => c.loginCount,
  joined: c => ms(c.setupAt),
  engagement: c => c.triedCount + c.favoritesCount + c.upcomingCount,
  submissions: c => c.submissionCount * 1000 + c.pendingSubmissionCount,
  attention: coupleAttention,
};

const REVIEWER_SORT: Record<ReviewerSortKey, (r: AdminReviewerSummary) => string | number> = {
  name: r => (r.code || r.id).toLowerCase(),
  lastActive: r => ms(lastActiveAt(r)),
  logins: r => r.loginCount,
  joined: r => ms(r.registeredAt),
  clipReviews: r => r.clipReviewCount,
  submissionReviews: r => r.submissionReviewCount,
  pending: reviewerPending,
};

function sortBy<T>(items: T[], get: (item: T) => string | number, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    return va < vb ? -sign : va > vb ? sign : 0;
  });
}

// ---- Small presentational pieces ----

const STATE_STYLE: Record<ActivityState, { dot: string; label: string }> = {
  online: { dot: 'bg-green-400 shadow-[0_0_0_3px_rgba(74,222,128,0.2)]', label: 'Online now' },
  active: { dot: 'bg-lime-400', label: 'Active in the last 7 days' },
  idle: { dot: 'bg-amber-400', label: 'Last active 7–30 days ago' },
  dormant: { dot: 'bg-zinc-600', label: 'No activity for 30+ days' },
};

const StatusDot: React.FC<{ state: ActivityState }> = ({ state }) => (
  <span title={STATE_STYLE[state].label} className={`inline-block w-2 h-2 rounded-full shrink-0 ${STATE_STYLE[state].dot}`} />
);

const SubText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{children}</div>
);

const Count: React.FC<{ icon: React.ElementType; value: number; title: string }> = ({ icon: Icon, value, title }) => (
  <span title={title} className={`inline-flex items-center gap-1 tabular-nums ${value > 0 ? 'text-zinc-200' : 'text-zinc-600'}`}>
    <Icon className="w-3.5 h-3.5" /> {value}
  </span>
);

const Badge: React.FC<{ tone: 'amber' | 'pink' | 'purple' | 'zinc'; title: string; children: React.ReactNode }> = ({ tone, title, children }) => {
  const tones = {
    amber: 'bg-amber-500/15 text-amber-300',
    pink: 'bg-pink-500/15 text-pink-300',
    purple: 'bg-purple-500/15 text-purple-300',
    zinc: 'bg-zinc-700/60 text-zinc-300',
  };
  return (
    <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium tabular-nums whitespace-nowrap ${tones[tone]}`}>
      {children}
    </span>
  );
};

const ProfilePill: React.FC<{ done: boolean; label: string; title: string }> = ({ done, label, title }) => (
  <span
    title={`${title}: ${done ? 'done' : 'not yet'}`}
    className={`h-5 px-1 inline-flex items-center justify-center rounded text-[10px] font-semibold ${done ? 'bg-pink-500/25 text-pink-200' : 'bg-zinc-800 text-zinc-600'}`}
  >
    {label}
  </span>
);

function SortHeader<K extends string>({ label, sortKey, sort, onSort, className = '' }: {
  label: string;
  sortKey: K;
  sort: { key: K; dir: SortDir };
  onSort: (key: K) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const Arrow = sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2 font-medium ${className}`}>
      <button
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-200 ${active ? 'text-zinc-200' : ''}`}
      >
        {label}
        {active && <Arrow className="w-3 h-3" />}
      </button>
    </th>
  );
}

function LastActiveCell({ p }: { p: Person }) {
  const state = activityState(p);
  let detail: string;
  if (state === 'online') {
    detail = `Online · ${p.currentPage === 'home' ? 'Home' : p.currentPage ?? 'app'}`;
  } else {
    const at = lastActiveAt(p);
    if (!at) {
      detail = 'No visits recorded';
    } else if (at === p.lastLoginAt) {
      const where = [p.lastLoginCity, p.lastLoginCountry].filter(Boolean).join(', ');
      detail = ['Login', deviceLabel(p.lastLoginUserAgent), where].filter(Boolean).join(' · ');
    } else if (at === p.lastSeen) {
      detail = `Last seen · ${p.currentPage === 'home' ? 'Home' : p.currentPage ?? 'app'}`;
    } else {
      detail = 'In-app activity';
    }
  }
  return (
    <td className="px-3 py-2.5">
      <div className={state === 'online' ? 'text-green-300' : 'text-zinc-200'}>{state === 'online' ? 'Now' : timeAgo(lastActiveAt(p))}</div>
      <SubText>{detail}</SubText>
    </td>
  );
}

function LoginsCell({ p, enabled }: { p: Person; enabled: boolean }) {
  return (
    <td className="px-3 py-2.5 tabular-nums">
      {enabled ? (
        <>
          <div className={p.loginCount ? 'text-zinc-200' : 'text-zinc-600'}>{p.loginCount}</div>
          <SubText>{p.loginsLast30d} in 30d</SubText>
        </>
      ) : (
        <span className="text-zinc-600">—</span>
      )}
    </td>
  );
}

// ---- Main component ----

export const PeopleOverview: React.FC<PeopleOverviewProps> = ({ people, loading, error, onRefresh, onOpenCouple, onOpenReviewer }) => {
  const [segment, setSegment] = useState<Segment>('couples');
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [coupleSort, setCoupleSort] = useState<{ key: CoupleSortKey; dir: SortDir }>({ key: 'lastActive', dir: 'desc' });
  const [reviewerSort, setReviewerSort] = useState<{ key: ReviewerSortKey; dir: SortDir }>({ key: 'lastActive', dir: 'desc' });

  const couples = people?.couples ?? [];
  const reviewers = people?.reviewers ?? [];
  const trackingEnabled = people?.loginTrackingEnabled ?? false;

  const everyone: Person[] = [...couples, ...reviewers];
  const stats = {
    online: everyone.filter(p => activityState(p) === 'online').length,
    active: everyone.filter(p => ['online', 'active'].includes(activityState(p))).length,
    dormant: everyone.filter(p => activityState(p) === 'dormant').length,
    pendingSubmissions: couples.reduce((n, c) => n + c.pendingSubmissionCount, 0),
  };

  const needsAttention = (p: Person) =>
    'coupleId' in p ? coupleAttention(p) > 0 : reviewerPending(p as AdminReviewerSummary) > 0;

  const matchesFilter = (p: Person) => {
    const state = activityState(p);
    switch (filter) {
      case 'online': return state === 'online';
      case 'active': return state === 'online' || state === 'active';
      case 'dormant': return state === 'dormant';
      case 'attention': return needsAttention(p);
      default: return true;
    }
  };

  const q = query.trim().toLowerCase();
  const visibleCouples = useMemo(() => {
    const rows = couples.filter(c => {
      if (!matchesFilter(c)) return false;
      if (!q) return true;
      return [coupleName(c), c.coupleId, c.ownerReviewer?.label, ...c.coaches.map(x => x.label)]
        .some(s => s?.toLowerCase().includes(q));
    });
    return sortBy(rows, COUPLE_SORT[coupleSort.key], coupleSort.dir);
  }, [couples, filter, q, coupleSort]);

  const visibleReviewers = useMemo(() => {
    const rows = reviewers.filter(r => {
      if (!matchesFilter(r)) return false;
      if (!q) return true;
      return [r.code, r.name, r.id, r.ownCouple?.label, r.managedCouple?.label].some(s => s?.toLowerCase().includes(q));
    });
    return sortBy(rows, REVIEWER_SORT[reviewerSort.key], reviewerSort.dir);
  }, [reviewers, filter, q, reviewerSort]);

  const toggleSort = <K extends string>(set: React.Dispatch<React.SetStateAction<{ key: K; dir: SortDir }>>) => (key: K) =>
    set(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));

  const segmentItems = segment === 'couples' ? couples : reviewers;
  const filterCounts: Record<Filter, number> = {
    all: segmentItems.length,
    online: segmentItems.filter(p => activityState(p) === 'online').length,
    active: segmentItems.filter(p => ['online', 'active'].includes(activityState(p))).length,
    dormant: segmentItems.filter(p => activityState(p) === 'dormant').length,
    attention: segmentItems.filter(needsAttention).length,
  };
  const filterLabels: Record<Filter, string> = {
    all: 'All',
    online: 'Online',
    active: 'Active 7d',
    dormant: 'Dormant 30d+',
    attention: segment === 'couples' ? 'Needs attention' : 'Has pending',
  };

  const rowProps = (onOpen: () => void) => ({
    onClick: onOpen,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(); },
    tabIndex: 0,
    className: 'border-t border-zinc-800 hover:bg-zinc-800/60 focus:bg-zinc-800/60 focus:outline-none cursor-pointer align-top',
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-white text-lg font-semibold">People</h3>
          <p className="text-zinc-400 text-sm">Every couple and reviewer, most recently active first. Click a row to open their full record.</p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {people ? `Updated ${new Date(people.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> Couldn't load people: {error}
        </div>
      )}

      {people && !trackingEnabled && (
        <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
          Login history isn't being recorded yet. Apply <code className="font-mono">d1-login-events.sql</code> to D1 to start;
          until then "Last active" uses presence heartbeats and in-app activity.
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Online now', value: stats.online, accent: 'text-green-300' },
          { label: 'Active in 7 days', value: stats.active, accent: 'text-lime-300' },
          { label: 'Dormant 30+ days', value: stats.dormant, accent: 'text-zinc-300' },
          { label: 'Pending couple submissions', value: stats.pendingSubmissions, accent: 'text-amber-300' },
        ].map(s => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3">
            <div className={`text-2xl font-semibold tabular-nums ${s.accent}`}>{people ? s.value : '–'}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="inline-flex p-1 bg-zinc-900 border border-zinc-700 rounded-lg shrink-0">
          {([['couples', HeartHandshake, couples.length], ['reviewers', Users, reviewers.length]] as const).map(([key, Icon, count]) => (
            <button
              key={key}
              onClick={() => { setSegment(key); setFilter('all'); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium capitalize ${segment === key ? 'text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
              style={segment === key ? { backgroundColor: COLORS.lime } : {}}
            >
              <Icon className="w-4 h-4" /> {key} <span className="tabular-nums opacity-70">{count}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={segment === 'couples' ? 'Search names, codes, reviewers…' : 'Search codes, names, couples…'}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {(Object.keys(filterLabels) as Filter[]).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2.5 py-1 rounded-full text-xs border ${filter === f ? 'border-zinc-400 bg-zinc-700 text-white' : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'}`}
          >
            {filterLabels[f]} <span className="tabular-nums opacity-60">{filterCounts[f]}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl overflow-x-auto">
        {!people && loading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 bg-zinc-800 rounded animate-pulse" />
            ))}
          </div>
        ) : segment === 'couples' ? (
          <table className="w-full min-w-[900px] text-sm">
            <thead className="text-[11px] text-zinc-500 text-left">
              <tr>
                <SortHeader label="Couple" sortKey="name" sort={coupleSort} onSort={toggleSort(setCoupleSort)} />
                <SortHeader label="Last active" sortKey="lastActive" sort={coupleSort} onSort={toggleSort(setCoupleSort)} />
                <SortHeader label="Logins" sortKey="logins" sort={coupleSort} onSort={toggleSort(setCoupleSort)} />
                <th className="px-3 py-2 font-medium uppercase tracking-wide">Reviewer</th>
                <SortHeader label="Engagement" sortKey="engagement" sort={coupleSort} onSort={toggleSort(setCoupleSort)} />
                <SortHeader label="Submissions" sortKey="submissions" sort={coupleSort} onSort={toggleSort(setCoupleSort)} />
                <th className="px-3 py-2 font-medium uppercase tracking-wide">Profile</th>
                <SortHeader label="Waiting" sortKey="attention" sort={coupleSort} onSort={toggleSort(setCoupleSort)} />
              </tr>
            </thead>
            <tbody>
              {visibleCouples.map(c => (
                <tr key={c.coupleId} {...rowProps(() => onOpenCouple(c.coupleId))}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <StatusDot state={activityState(c)} />
                      <span className="text-white font-medium truncate max-w-[180px]">{coupleName(c)}</span>
                    </div>
                    <SubText><span className="font-mono">{c.coupleId}</span> · joined {shortDate(c.setupAt)}</SubText>
                  </td>
                  <LastActiveCell p={c} />
                  <LoginsCell p={c} enabled={trackingEnabled} />
                  <td className="px-3 py-2.5">
                    <div className="flex flex-col items-start gap-1">
                      {c.coaches.map(coach => (
                        <button
                          key={coach.id}
                          onClick={e => { e.stopPropagation(); onOpenReviewer(coach.id); }}
                          className="text-[11px] px-1.5 py-0.5 rounded-md bg-lime-500/15 text-lime-300 hover:bg-lime-500/25 whitespace-nowrap"
                          title="Reviewer coaching this couple"
                        >
                          Coach · {coach.label}
                        </button>
                      ))}
                      {c.ownerReviewer && (
                        <button
                          onClick={e => { e.stopPropagation(); onOpenReviewer(c.ownerReviewer!.id); }}
                          className="text-[11px] px-1.5 py-0.5 rounded-md bg-pink-500/15 text-pink-300 hover:bg-pink-500/25 whitespace-nowrap"
                          title="This is the reviewer's own couple profile"
                        >
                          Own profile · {c.ownerReviewer.label}
                        </button>
                      )}
                      {c.coaches.length === 0 && !c.ownerReviewer && <span className="text-zinc-600 text-xs">None</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-3 text-xs">
                      <Count icon={Check} value={c.triedCount} title="Clips tried" />
                      <Count icon={Star} value={c.favoritesCount} title="Favourites" />
                      <Count icon={Calendar} value={c.upcomingCount} title={`Upcoming dates (${c.completedCount} completed)`} />
                    </div>
                    <SubText>{c.lastTriedAt ? `last tried ${timeAgo(c.lastTriedAt)}` : `${c.wishlistCount} on wishlist`}</SubText>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className={`tabular-nums ${c.submissionCount ? 'text-zinc-200' : 'text-zinc-600'}`}>{c.submissionCount}</span>
                      {c.pendingSubmissionCount > 0 && <Badge tone="amber" title="Awaiting review">{c.pendingSubmissionCount} pending</Badge>}
                    </div>
                    <SubText>{c.lastSubmissionAt ? `last ${timeAgo(c.lastSubmissionAt)}` : 'none yet'}</SubText>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <ProfilePill done={c.hasHisProfile} label="His" title="His profile" />
                      <ProfilePill done={c.hasHerProfile} label="Her" title="Her profile" />
                      <ProfilePill done={!!c.surveyCompletedAt} label="Srv" title="Her survey" />
                      <ProfilePill done={!!c.feedbackSubmittedAt} label="Fb" title="General feedback" />
                    </div>
                    <SubText>{c.preferencesCount} category prefs</SubText>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {c.unreadFromCoupleCount > 0 && <Badge tone="pink" title="Unread messages from the couple"><MessageSquare className="w-3 h-3" />{c.unreadFromCoupleCount}</Badge>}
                      {c.pendingRequestCount > 0 && <Badge tone="purple" title="Pending content requests"><Film className="w-3 h-3" />{c.pendingRequestCount}</Badge>}
                      {c.awaitingCoupleCount > 0 && <Badge tone="zinc" title="Questions waiting on the couple's answer"><Send className="w-3 h-3" />{c.awaitingCoupleCount}</Badge>}
                      {coupleAttention(c) === 0 && c.awaitingCoupleCount === 0 && <span className="text-zinc-600 text-xs">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {visibleCouples.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-zinc-500">No couples match.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full min-w-[900px] text-sm">
            <thead className="text-[11px] text-zinc-500 text-left">
              <tr>
                <SortHeader label="Reviewer" sortKey="name" sort={reviewerSort} onSort={toggleSort(setReviewerSort)} />
                <SortHeader label="Last active" sortKey="lastActive" sort={reviewerSort} onSort={toggleSort(setReviewerSort)} />
                <SortHeader label="Logins" sortKey="logins" sort={reviewerSort} onSort={toggleSort(setReviewerSort)} />
                <th className="px-3 py-2 font-medium uppercase tracking-wide">Couples</th>
                <SortHeader label="Clip reviews" sortKey="clipReviews" sort={reviewerSort} onSort={toggleSort(setReviewerSort)} />
                <SortHeader label="Submission reviews" sortKey="submissionReviews" sort={reviewerSort} onSort={toggleSort(setReviewerSort)} />
                <SortHeader label="Pending" sortKey="pending" sort={reviewerSort} onSort={toggleSort(setReviewerSort)} />
              </tr>
            </thead>
            <tbody>
              {visibleReviewers.map(r => (
                <tr key={r.id} {...rowProps(() => onOpenReviewer(r.id))}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <StatusDot state={activityState(r)} />
                      <span className="text-white font-medium font-mono">{r.code || r.id}</span>
                      {r.name && <span className="text-zinc-400 truncate max-w-[120px]">{r.name}</span>}
                    </div>
                    <SubText>{[r.mode, r.gender, `joined ${shortDate(r.registeredAt)}`].filter(Boolean).join(' · ')}</SubText>
                  </td>
                  <LastActiveCell p={r} />
                  <LoginsCell p={r} enabled={trackingEnabled} />
                  <td className="px-3 py-2.5">
                    <div className="flex flex-col items-start gap-1">
                      {r.managedCouple && (
                        <button
                          onClick={e => { e.stopPropagation(); onOpenCouple(r.managedCouple!.id); }}
                          className="text-[11px] px-1.5 py-0.5 rounded-md bg-lime-500/15 text-lime-300 hover:bg-lime-500/25"
                          title="Couple this reviewer coaches"
                        >
                          Coaching · {r.managedCouple.label}
                        </button>
                      )}
                      {r.ownCouple && (
                        <button
                          onClick={e => { e.stopPropagation(); onOpenCouple(r.ownCouple!.id); }}
                          className="text-[11px] px-1.5 py-0.5 rounded-md bg-pink-500/15 text-pink-300 hover:bg-pink-500/25"
                          title="Reviewer's own couple profile"
                        >
                          Own · {r.ownCouple.label}
                        </button>
                      )}
                      {!r.managedCouple && !r.ownCouple && <span className="text-zinc-600 text-xs">None</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className={`tabular-nums ${r.clipReviewCount ? 'text-zinc-200' : 'text-zinc-600'}`}>{r.clipReviewCount}</div>
                    <SubText>{r.lastClipReviewAt ? `last ${timeAgo(r.lastClipReviewAt)}` : 'none yet'}</SubText>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className={`tabular-nums ${r.submissionReviewCount ? 'text-zinc-200' : 'text-zinc-600'}`}>{r.submissionReviewCount}</div>
                    <SubText>{r.lastSubmissionReviewAt ? `last ${timeAgo(r.lastSubmissionReviewAt)}` : 'none yet'}</SubText>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {r.pendingFollowUpCount > 0 && <Badge tone="amber" title="Unanswered follow-up questions"><MessageSquare className="w-3 h-3" />{r.pendingFollowUpCount}</Badge>}
                      {r.pendingEditCount > 0 && <Badge tone="purple" title="Suggested edits awaiting approval"><Edit2 className="w-3 h-3" />{r.pendingEditCount}</Badge>}
                      {r.unreadTutorialCount > 0 && <Badge tone="zinc" title="Unread tag tutorials"><BookOpen className="w-3 h-3" />{r.unreadTutorialCount}</Badge>}
                      {reviewerPending(r) === 0 && <span className="text-zinc-600 text-xs">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
              {visibleReviewers.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-zinc-500">No reviewers match.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
